import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';
import { buildEventsApiUrl } from '../config/api';
import { isSiteAdminUser } from '../config/admin';
import { buildAuthHeaders } from '../utils/authFetch';

const SiteAdminHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);
  const isAllowed = isSiteAdminUser(user as any);
  const [stats, setStats] = useState<{
    events: number;
    items: number;
    tradeGroups: number;
    readyTradeGroups: number;
    completedTradeGroups: number;
    sessions: number;
    activeSessions: number;
    completedSessions: number;
    canceledSessions: number;
    checkedInParticipants: number;
  } | null>(null);
  const [serviceHealth, setServiceHealth] = useState<{ services: any[]; scheduledTasks: any[] }>({ services: [], scheduledTasks: [] });
  const [operations, setOperations] = useState<{ activeSessions: any[]; readyGroups: any[]; recentIssues: any[]; recentReviews: any[] }>({
    activeSessions: [],
    readyGroups: [],
    recentIssues: [],
    recentReviews: [],
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await fetch(buildEventsApiUrl('/api/site-admin/stats'), {
          headers: buildAuthHeaders(user),
        });
        const data = await response.json();
        if (!response.ok) return;
        setStats(data.stats || null);
        setOperations(data.operations || { activeSessions: [], readyGroups: [], recentIssues: [], recentReviews: [] });
        setServiceHealth(data.serviceHealth || { services: [], scheduledTasks: [] });
      } catch {
        // leave stats empty if unavailable
      }
    };
    loadStats();
  }, [user]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={2.5}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Site Admin</Typography>
          <Typography variant="body2" color="text.secondary">
            Early admin surface for runtime controls and future operational tools.
          </Typography>

          {!isAllowed && (
            <Alert severity="warning">
              You are signed in as <strong>{userId || 'unknown'}</strong>. This area is intended for site admins.
            </Alert>
          )}

          {stats && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="h6">Cool stats</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
                  <StatCard label="Events" value={stats.events} />
                  <StatCard label="Items" value={stats.items} />
                  <StatCard label="Trade Groups" value={stats.tradeGroups} />
                  <StatCard label="Ready Groups" value={stats.readyTradeGroups} />
                  <StatCard label="Completed Groups" value={stats.completedTradeGroups} />
                  <StatCard label="Sessions" value={stats.sessions} />
                  <StatCard label="Active Sessions" value={stats.activeSessions} />
                  <StatCard label="Completed Sessions" value={stats.completedSessions} />
                  <StatCard label="Canceled Sessions" value={stats.canceledSessions} />
                  <StatCard label="Checked-In Participants" value={stats.checkedInParticipants} />
                </Box>
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6">Service health</Typography>
              <Typography variant="body2" color="text.secondary">
                Live status for frontend/backend services plus the Windows watchdog tasks.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                {serviceHealth.services.map((service) => (
                  <HealthCard
                    key={service.name}
                    label={service.name}
                    healthy={Boolean(service.healthy)}
                    status={service.status}
                    detail={service.restarts != null ? `restarts: ${service.restarts}` : service.detail}
                  />
                ))}
                {serviceHealth.scheduledTasks.map((task) => (
                  <HealthCard
                    key={task.name}
                    label={task.name}
                    healthy={Boolean(task.healthy)}
                    status={task.status}
                    detail={task.lastResult ? `last result: ${task.lastResult}` : task.detail}
                  />
                ))}
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6">Available now</Typography>
              <Typography variant="body2" color="text.secondary">
                Runtime settings is the first live-backed admin feature. It currently controls event-day polling intervals for participant and admin views.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <BoxButton to="/site-admin/runtime-settings" label="Open Runtime Settings" disabled={!isAllowed} />
                <BoxButton to="/site-admin/operations" label="Open Event-Day Operations" disabled={!isAllowed} />
                <BoxButton to="/site-admin/trade-session-metrics" label="Open Trade Session Metrics" disabled={!isAllowed} />
                <BoxButton to="/site-admin/users" label="Manage Site Admin Users" disabled={!isAllowed} />
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6">Event-day operations</Typography>
              <Typography variant="body2" color="text.secondary">
                Quick snapshot of the most recent live sessions, ready trade groups, issues, and reviews.
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Active / staged sessions</Typography>
                  {operations.activeSessions.length > 0 ? operations.activeSessions.map((session) => (
                    <Button key={session.id} variant="text" onClick={() => navigate(`/events/${session.eventId}`)} sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0 }}>
                      {session.locationName || 'Unassigned location'} · {session.status} · event {session.eventId}
                    </Button>
                  )) : (
                    <Typography variant="body2" color="text.secondary">No active or staged sessions right now.</Typography>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Ready trade groups</Typography>
                  {operations.readyGroups.length > 0 ? operations.readyGroups.map((group) => (
                    <Button key={group.id} variant="text" onClick={() => navigate(`/events/${group.eventId}`)} sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0 }}>
                      Group {group.groupKey} · {group.participantCount || group.participantUserIds?.length || 0} participants · event {group.eventId}
                    </Button>
                  )) : (
                    <Typography variant="body2" color="text.secondary">No ready groups right now.</Typography>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Recent issues / behavior records</Typography>
                  {operations.recentIssues.length > 0 ? operations.recentIssues.map((issue) => (
                    <Button key={issue.id} variant="text" onClick={() => navigate(`/events/${issue.eventId}`)} sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0 }}>
                      {issue.outcomeType || 'behavior-record'} · event {issue.eventId}
                    </Button>
                  )) : (
                    <Typography variant="body2" color="text.secondary">No recent issue records right now.</Typography>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Recent reviews</Typography>
                  {operations.recentReviews.length > 0 ? operations.recentReviews.map((review) => (
                    <Button key={review.id} variant="text" onClick={() => navigate(`/events/${review.eventId}`)} sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0 }}>
                      {review.reviewStatus || 'review'} · {review.rating || 0}★ · event {review.eventId}
                    </Button>
                  )) : (
                    <Typography variant="body2" color="text.secondary">No recent reviews right now.</Typography>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        </Stack>
      </Paper>
    </Container>
  );
};

const StatCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Paper>
);

const HealthCard: React.FC<{ label: string; healthy: boolean; status?: string; detail?: string }> = ({ label, healthy, status, detail }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: healthy ? 'success.light' : 'error.light', bgcolor: healthy ? 'rgba(46,125,50,0.06)' : 'rgba(211,47,47,0.06)' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: healthy ? 'success.dark' : 'error.dark', fontWeight: 600, mb: 0.5 }}>
      {healthy ? '● Healthy' : '● Needs attention'}
    </Typography>
    {status && <Typography variant="caption" display="block" color="text.secondary">{status}</Typography>}
    {detail && <Typography variant="caption" display="block" color="text.secondary">{detail}</Typography>}
  </Paper>
);

const BoxButton: React.FC<{ to: string; label: string; disabled?: boolean }> = ({ to, label, disabled }) => (
  <Button component={RouterLink} to={to} variant="contained" disabled={disabled} sx={{ alignSelf: 'flex-start' }}>
    {label}
  </Button>
);

export default SiteAdminHomePage;
